/**
 * 🎯 EXECUTIVE DASHBOARD GRID
 * ════════════════════════════════════════════════════════════════════════
 * 
 * RESPONSABILIDAD:
 * Renderizar widgets en grid CSS (12 columnas) usando layouts del backend.
 * 
 * REGLAS INMUTABLES:
 * ✅ Usar layouts que vienen del backend sin modificar
 * ✅ Renderizar widgets en el orden exacto recibido
 * ❌ NO calcular posiciones
 * ❌ NO reordenar
 * ❌ NO aplicar clamps/min/max
 * ❌ NO hacer fallbacks manuales
 * 
 * Si el layout es incorrecto → es bug del backend (y está bien que se vea mal).
 */

import React from 'react';
import type { ExecutiveWidget } from '../types';
import ExecutiveWidgetRenderer from './ExecutiveWidgetRenderer';
import EditableWidgetWrapper from './EditableWidgetWrapper';
import LazyWidget from '../../../shared/ui/LazyWidget';
import '../styles/grid.css';

interface ExecutiveDashboardGridProps {
  widgets: ExecutiveWidget[];
  isEditing?: boolean;
  newWidgetId?: string | null;
  onRemoveWidget?: (widgetId: string) => void;
  onDuplicateWidget?: (widgetId: string) => void;
}

const ExecutiveDashboardGrid: React.FC<ExecutiveDashboardGridProps> = ({ 
  widgets,
  isEditing = false,
  newWidgetId = null,
  onRemoveWidget,
  onDuplicateWidget
}) => {
  
  // Log para debugging
  console.log('📊 ExecutiveDashboardGrid rendering:', {
    widgetCount: widgets.length,
    isEditing,
    order: widgets.map(w => ({ id: w.id, type: w.type, role: w.role }))
  });
  
  return (
    <div className="executive-grid-container">
      {widgets.map((widget, index) => {
        
        const { layout } = widget;
        
        // Confianza ciega: usar layout tal cual viene
        const gridStyle: React.CSSProperties = {
          gridColumn: `${layout.x + 1} / span ${layout.w}`,
          gridRow: `${layout.y + 1} / span ${layout.h}`,
        };
        
        const widgetContent = (
          <div
            key={widget.id}
            className="executive-widget-container"
            style={gridStyle}
          >
            <LazyWidget placeholder={<div className="widget-placeholder">Cargando widget...</div>}>
              <ExecutiveWidgetRenderer widget={widget} index={index} />
            </LazyWidget>
          </div>
        );
        
        // Wrap with edit controls if in EDIT mode
        if (isEditing && onRemoveWidget && onDuplicateWidget) {
          return (
            <EditableWidgetWrapper
              key={widget.id}
              widget={widget}
              isEditing={isEditing}
              isNew={widget.id === newWidgetId}
              onRemove={onRemoveWidget}
              onDuplicate={onDuplicateWidget}
            >
              {widgetContent}
            </EditableWidgetWrapper>
          );
        }
        
        return widgetContent;
      })}
    </div>
  );
};

export default ExecutiveDashboardGrid;
